import type { SVGAttributes } from 'react'
import { cx } from './cx'
import s from './Sparkline.module.css'

export type SparklineTone = 'brand' | 'good' | 'warn' | 'critical'

export interface SparklineProps extends Omit<SVGAttributes<SVGSVGElement>, 'points'> {
  /**
   * The series, oldest first, exactly as the HOST read it. The line is drawn
   * from these numbers and from nothing else: no smoothing, no padding, no
   * point invented to make a short series look like a trend.
   */
  points: readonly number[]
  /**
   * What the line says, in words — "Active users, last 12 weeks, rising". The
   * line is an image; this is its text alternative, and it is REQUIRED because
   * a trend a screen reader cannot hear is a trend only some readers get.
   */
  label: string
  tone?: SparklineTone
}

/** Whether a series is a trend at all: two or more FINITE points. Shared with
 *  the tiles that host a sparkline, so a tile never draws an empty slot for a
 *  line that will refuse to draw. Module-internal — not on the barrel. */
export function drawablePoints(points: readonly number[]): number[] {
  return points.filter((value) => Number.isFinite(value))
}

/** The drawing box. The SVG stretches to its container (`preserveAspectRatio`
 *  none) and the stroke does not scale with it, so these are proportions, not
 *  pixels. */
const W = 100
const H = 30
/** Room above and below the line so its stroke is never clipped at the extremes. */
const PAD = 2

/**
 * A TREND LINE FROM A SERIES THE HOST SUPPLIES (#2406, "Dashboards and Cards":
 * the metric card "with trend" and the strip's cells).
 *
 * ── the rule, before the geometry ──────────────────────────────────────────
 * The design draws a sparkline under every figure, and the design's own
 * placeholder note says every trend on that sheet is invented. A kit sparkline
 * that drew something when it was handed nothing would carry that invention
 * into the product. So: fewer than two FINITE points is not a trend, and the
 * component renders NOTHING — not a flat line, not a dashed placeholder, both
 * of which read as "measured, and unchanged". A non-finite value (a `null`
 * coerced to `NaN`, an `Infinity` from a division) is dropped rather than
 * drawn as zero, for the reason `StatStrip` gives: absent is not zero.
 *
 * It is not `InsightChart`. That is a governed chart with axes, a data table
 * and a drill; this is a mark beside a number, with no axis a reader could
 * read a value off — which is why it takes a sentence, not a table.
 */
export function Sparkline({ points, label, tone = 'brand', className, ...rest }: SparklineProps) {
  const finite = drawablePoints(points)
  if (finite.length < 2) return null
  const min = Math.min(...finite)
  const max = Math.max(...finite)
  const span = max - min
  const x = (index: number) => (index / (finite.length - 1)) * W
  /* A flat series is a real answer ("unchanged"), drawn through the middle —
     not divided by a zero span into NaN. */
  const y = (value: number) => span === 0 ? H / 2 : PAD + (1 - (value - min) / span) * (H - PAD * 2)
  const line = finite.map((value, index) => `${x(index).toFixed(2)},${y(value).toFixed(2)}`).join(' ')
  const area = `0,${H} ${line} ${W},${H}`
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className={cx(s.spark, s[tone], className)}
      role="img"
      aria-label={label}
      data-sparkline=""
      {...rest}
    >
      <polygon className={s.area} points={area} />
      <polyline className={s.line} points={line} vectorEffect="non-scaling-stroke" />
    </svg>
  )
}
